/**
 * Cursor-paginated listing for a single event, exposed as enriched MediaItems.
 *
 * Replaces the legacy "fetch the entire bucket into memory up front" model
 * with React Query's useInfiniteQuery driven by nextContinuationToken.
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import { listFiles } from '../lib/apiClient.ts';
import { eventPrefix } from '../lib/keys.ts';
import { buildMediaItems } from '../lib/mediaItem.ts';
import { UNCATEGORIZED_EVENT } from '../config/awsConfig.ts';
import type { MediaItem } from '../lib/types.ts';

const PAGE_SIZE = 100;

interface Page {
  items: MediaItem[];
  nextToken?: string;
}

/** Paginated media listing for an event (lists its thumbnail prefix). */
export function useFileList(event: string) {
  return useInfiniteQuery({
    queryKey: ['files', event],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }): Promise<Page> => {
      // Uncategorized = files directly under thumb_uploads/ with no event
      // folder. Pass delimiter='/' so S3 doesn't recurse into nested event
      // folders — otherwise "Uncategorized" would leak every other event's
      // photos into its listing.
      const isUncategorized = event === UNCATEGORIZED_EVENT;
      const page = await listFiles({
        prefix: eventPrefix(event, 'thumbnail'),
        maxKeys: PAGE_SIZE,
        continuationToken: pageParam,
        delimiter: isUncategorized ? '/' : undefined,
        signal,
      });
      return { items: buildMediaItems(page.files), nextToken: page.nextContinuationToken };
    },
    getNextPageParam: (last) => last.nextToken,
    staleTime: 30_000,
  });
}

/** Flatten the infinite-query pages into a single MediaItem array. */
export function flattenPages(pages: Page[] | undefined): MediaItem[] {
  if (!pages) return [];
  return pages.flatMap((p) => p.items);
}
